<script setup>
import TableRow from "@/components/global-themed/table/TableRow.vue";
import HintSquare from "@/components/global-themed/HintSquare.vue";
import CompetitorCell from "@/components/competition/partials/compare/CompetitorCell.vue";
import TableCell from "@/components/global-themed/table/TableCell.vue";
import TableBody from "@/components/global-themed/table/TableBody.vue";
import {useStore} from "vuex";
import {computed} from "vue";

const store = useStore();
const websiteCompareData = computed(() => store.getters['competitionV2/websiteCompareData']);
const compareCompetitorColumns = computed(() => store.getters["competitionV2/compareCompetitorColumns"]);

</script>

<template>
  <TableBody>
    <TableRow>
      <TableCell
        col-span="2"
        class="tw-flex tw-items-center tw-gap-2"
      >
        <span>Total Backlinks</span>
        <HintSquare hint="The total number of external backlinks pointing to the website" />
      </TableCell>

      <TableCell
        col-span="2"
        class="tw-font-medium tw-text-center"
      >
        {{ websiteCompareData?.totalBacklinks ?? 'N/A' }}
      </TableCell>

      <CompetitorCell
        v-for="column in compareCompetitorColumns"
        :key="column"
        name="totalBacklinks"
        :column="column"
      />
    </TableRow>

    <TableRow>
      <TableCell
        col-span="2"
        class="tw-flex tw-items-center tw-gap-2"
      >
        <span>Referring Domains</span>
        <HintSquare hint="The total number of external domains who link to the website" />
      </TableCell>

      <TableCell
        col-span="2"
        class="tw-font-medium tw-text-center"
      >
        {{ websiteCompareData?.referringDomains ?? 'N/A' }}
      </TableCell>

      <CompetitorCell
        v-for="column in compareCompetitorColumns"
        :key="column"
        name="referringDomains"
        :column="column"
      />
    </TableRow>

    <TableRow>
      <TableCell
        col-span="2"
        class="tw-flex tw-items-center tw-gap-2"
      >
        <span>Trust Flow</span>
        <HintSquare
          hint="A score based on the quality and relevance of backlinks pointing to the website. The more relevant backlinks you have, the higher your Trust Flow score."
        />
      </TableCell>

      <TableCell
        col-span="2"
        class="tw-font-medium tw-text-center"
      >
        {{ websiteCompareData?.trustFlow ?? 'N/A' }}
      </TableCell>

      <CompetitorCell
        v-for="column in compareCompetitorColumns"
        :key="column"
        name="trustFlow"
        :column="column"
      />
    </TableRow>

    <TableRow>
      <TableCell
        col-span="2"
        class="tw-flex tw-items-center tw-gap-2"
      >
        <span>Citation Flow</span>
        <HintSquare
          hint="A score based on the quantity and popularity of backlinks pointing to the website.
            The more popular backlinks you have, the higher your Citation Flow score."
        />
      </TableCell>

      <TableCell
        col-span="2"
        class="tw-font-medium tw-text-center"
      >
        {{ websiteCompareData?.citationFlow ?? 'N/A' }}
      </TableCell>

      <CompetitorCell
        v-for="column in compareCompetitorColumns"
        :key="column"
        name="citationFlow"
        :column="column"
      />
    </TableRow>

    <TableRow>
      <TableCell
        col-span="2"
        class="tw-flex tw-items-center tw-gap-2"
      >
        <span>Domain Authority</span>
        <HintSquare
          hint="Domain Authority is a metric that is unique to us, we use our own algorithms to calculate an overall score
            based on a combination of your Trust Flow and Citation Flow."
        />
      </TableCell>

      <TableCell
        col-span="2"
        class="tw-font-medium tw-text-center"
      >
        {{ websiteCompareData?.domainAuthority ?? 'N/A' }}
      </TableCell>

      <CompetitorCell
        v-for="column in compareCompetitorColumns"
        :key="column"
        name="domainAuthority"
        :column="column"
      />
    </TableRow>
  </TableBody>
</template>